import { useState, useEffect, useRef } from "react";
import { Plus, Pencil, Trash2, Building2, Upload, Loader2, X, Check, XCircle, Clock, MapPin } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Label } from "@/components/ui/label";
import { Badge } from "@/components/ui/badge";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from "@/components/ui/alert-dialog";
import {
  Tabs,
  TabsContent,
  TabsList,
  TabsTrigger,
} from "@/components/ui/tabs";
import { toast } from "sonner";
import AdminLayout from "@/components/layout/AdminLayout";
import { LocationInput } from "@/components/shared/LocationPickerV2";
import axios from "axios";
import { API } from "@/App";
import AdminPagination from "@/components/admin/AdminPagination";

const getImageUrl = (url) => {
  if (!url) return null;
  if (url.startsWith('http')) return url;
  const backendUrl = process.env.REACT_APP_BACKEND_URL || '';
  if (url.startsWith('/uploads/')) {
    return `${backendUrl}/api${url}`;
  }
  return `${backendUrl}${url}`;
};

const statusColors = {
  pending: "bg-yellow-100 text-yellow-700",
  published: "bg-green-100 text-green-700",
  rejected: "bg-red-100 text-red-700",
};

const statusLabels = {
  pending: "Pendiente",
  published: "Publicado",
  rejected: "Rechazado",
};

export default function AdminDirectorio() {
  const [businesses, setBusinesses] = useState([]);
  const [categories, setCategories] = useState([]);
  const [loading, setLoading] = useState(true);
  const [page, setPage] = useState(1);
  const [limit, setLimit] = useState(25);
  const [total, setTotal] = useState(0);
  const [dialogOpen, setDialogOpen] = useState(false);
  const [deleteDialogOpen, setDeleteDialogOpen] = useState(false);
  const [selectedBusiness, setSelectedBusiness] = useState(null);
  const [saving, setSaving] = useState(false);
  const [mediaList, setMediaList] = useState([]);
  const [mediaDialogOpen, setMediaDialogOpen] = useState(false);
  const [mediaSelectMode, setMediaSelectMode] = useState("featured");
  const [uploading, setUploading] = useState(false);
  const [activeTab, setActiveTab] = useState("all");
  const fileInputRef = useRef(null);

  const [formData, setFormData] = useState({
    name: "",
    category: "",
    description: "",
    services: "",
    phone: "",
    whatsapp: "",
    email: "",
    address: "",
    municipio: "",
    departamento: "",
    lat: null,
    lng: null,
    website: "",
    facebook: "",
    instagram: "",
    tiktok: "",
    hours: "",
    featured_image_id: "",
    featured_image_url: "",
    gallery_image_ids: [],
    gallery_urls: [],
    status: "draft",
  });

  const token = localStorage.getItem("session_token");
  const headers = { Authorization: `Bearer ${token}` };

  useEffect(() => {
    fetchCategories();
    fetchBusinesses();
  }, [activeTab, page, limit]);

  const fetchCategories = async () => {
    try {
      const response = await axios.get(`${API}/business/categories`);
      setCategories(response.data.categories);
    } catch (error) {
      console.error("Error:", error);
    }
  };

  const fetchBusinesses = async () => {
    try {
      let url = `${API}/businesses?skip=${(page - 1) * limit}&limit=${limit}`;
      if (activeTab !== "all") url += `&status=${activeTab}`;
      
      const response = await axios.get(url, { headers });
      setBusinesses(response.data.items);
      setTotal(response.data.total || 0);
    } catch (error) {
      console.error("Error:", error);
    } finally {
      setLoading(false);
    }
  };

  const fetchMedia = async () => {
    try {
      const response = await axios.get(`${API}/media?limit=100`, { headers });
      setMediaList(response.data.items);
    } catch (error) {
      console.error("Error:", error);
    }
  };

  const handleOpenDialog = (business = null) => {
    if (business) {
      setSelectedBusiness(business);
      setFormData({
        name: business.name || "",
        category: business.category || "",
        description: business.description || "",
        services: Array.isArray(business.services) ? business.services.join(", ") : "",
        phone: business.phone || "",
        whatsapp: business.whatsapp || "",
        email: business.email || "",
        address: business.address || "",
        municipio: business.municipio || "",
        departamento: business.departamento || "",
        lat: business.lat || null,
        lng: business.lng || null,
        website: business.website || "",
        facebook: business.facebook || "",
        instagram: business.instagram || "",
        tiktok: business.tiktok || "",
        hours: business.hours || "",
        featured_image_id: business.featured_image_id || "",
        featured_image_url: business.featured_image_url || "",
        gallery_image_ids: business.gallery_image_ids || [],
        gallery_urls: business.gallery_urls || [],
        status: business.status || "draft",
      });
    } else {
      setSelectedBusiness(null);
      setFormData({
        name: "", category: "", description: "", services: "",
        phone: "", whatsapp: "", email: "", address: "", municipio: "", departamento: "",
        lat: null, lng: null,
        website: "", facebook: "", instagram: "", tiktok: "", hours: "",
        featured_image_id: "", featured_image_url: "",
        gallery_image_ids: [], gallery_urls: [], status: "draft",
      });
    }
    setDialogOpen(true);
  };

  const handleOpenMediaDialog = (mode) => {
    setMediaSelectMode(mode);
    fetchMedia();
    setMediaDialogOpen(true);
  };

  const handleMediaSelect = (media) => {
    let imageUrl = media.url;
    if (imageUrl && imageUrl.startsWith('/uploads/')) {
      imageUrl = `${process.env.REACT_APP_BACKEND_URL}/api${imageUrl}`;
    }

    if (mediaSelectMode === "featured") {
      setFormData({
        ...formData,
        featured_image_id: media.media_id,
        featured_image_url: imageUrl,
      });
      setMediaDialogOpen(false);
    } else {
      if (!formData.gallery_image_ids.includes(media.media_id)) {
        setFormData({
          ...formData,
          gallery_image_ids: [...formData.gallery_image_ids, media.media_id],
          gallery_urls: [...formData.gallery_urls, { id: media.media_id, url: imageUrl }],
        });
      }
    }
  };

  const handleRemoveGalleryImage = (mediaId) => {
    setFormData({
      ...formData,
      gallery_image_ids: formData.gallery_image_ids.filter((id) => id !== mediaId),
      gallery_urls: formData.gallery_urls.filter((img) => img.id !== mediaId),
    });
  };

  const handleFileUpload = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;

    setUploading(true);
    try {
      const formDataUpload = new FormData();
      formDataUpload.append('file', file);
      formDataUpload.append('title', file.name.replace(/\.[^/.]+$/, ''));

      const response = await axios.post(`${API}/media/upload`, formDataUpload, {
        headers: { ...headers, 'Content-Type': 'multipart/form-data' }
      });

      toast.success("Imagen subida");
      await fetchMedia();
      handleMediaSelect(response.data);
    } catch (error) {
      toast.error("Error al subir");
    } finally {
      setUploading(false);
      if (fileInputRef.current) fileInputRef.current.value = '';
    }
  };

  const handleQuickStatus = async (business, newStatus) => {
    try {
      const payload = {
        name: business.name,
        category: business.category,
        description: business.description,
        services: business.services || [],
        phone: business.phone,
        whatsapp: business.whatsapp,
        email: business.email,
        address: business.address,
        municipio: business.municipio,
        departamento: business.departamento,
        lat: business.lat,
        lng: business.lng,
        website: business.website,
        facebook: business.facebook,
        instagram: business.instagram,
        tiktok: business.tiktok,
        hours: business.hours,
        featured_image_id: business.featured_image_id,
        gallery_image_ids: business.gallery_image_ids || [],
        status: newStatus,
      };
      
      await axios.put(`${API}/businesses/${business.business_id}`, payload, { headers });
      toast.success(newStatus === "published" ? "Empresa aprobada" : "Empresa rechazada");
      fetchBusinesses();
    } catch (error) {
      toast.error("Error al actualizar");
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!formData.name || !formData.category) {
      toast.error("Nombre y categoría son requeridos");
      return;
    }

    setSaving(true);
    try {
      const payload = {
        ...formData,
        services: formData.services ? formData.services.split(',').map(s => s.trim()) : [],
        description: formData.description || null,
        phone: formData.phone || null,
        whatsapp: formData.whatsapp || null,
        email: formData.email || null,
        address: formData.address || null,
        municipio: formData.municipio || null,
        departamento: formData.departamento || null,
        lat: formData.lat || null,
        lng: formData.lng || null,
        website: formData.website || null,
        facebook: formData.facebook || null,
        instagram: formData.instagram || null,
        tiktok: formData.tiktok || null,
        hours: formData.hours || null,
        featured_image_id: formData.featured_image_id || null,
      };

      if (selectedBusiness) {
        await axios.put(`${API}/businesses/${selectedBusiness.business_id}`, payload, { headers });
        toast.success("Empresa actualizada");
      } else {
        await axios.post(`${API}/businesses`, payload, { headers });
        toast.success("Empresa creada");
      }

      setDialogOpen(false);
      fetchBusinesses();
    } catch (error) {
      toast.error("Error al guardar");
    } finally {
      setSaving(false);
    }
  };

  const handleDelete = async () => {
    if (!selectedBusiness) return;
    try {
      await axios.delete(`${API}/businesses/${selectedBusiness.business_id}`, { headers });
      toast.success("Empresa eliminada");
      setDeleteDialogOpen(false);
      fetchBusinesses();
    } catch (error) {
      toast.error("Error al eliminar");
    }
  };

  const pendingCount = businesses.filter(b => b.status === "pending").length;

  return (
    <AdminLayout>
      <div className="space-y-6">
        <div className="flex items-center justify-between">
          <div>
            <h1 className="text-2xl font-bold text-brand-text">Directorio de Empresas</h1>
            <p className="text-brand-textSecondary">Gestiona el directorio de negocios</p>
          </div>
          <Button onClick={() => handleOpenDialog()} data-testid="new-business-btn">
            <Plus className="h-4 w-4 mr-2" />
            Nueva Empresa
          </Button>
        </div>

        {/* Tabs */}
        <Tabs value={activeTab} onValueChange={setActiveTab}>
          <TabsList>
            <TabsTrigger value="all">Todas</TabsTrigger>
            <TabsTrigger value="pending" className="flex items-center gap-2">
              Pendientes
              {pendingCount > 0 && (
                <span className="w-5 h-5 bg-yellow-500 text-white text-xs rounded-full flex items-center justify-center">
                  {pendingCount}
                </span>
              )}
            </TabsTrigger>
            <TabsTrigger value="published">Publicadas</TabsTrigger>
            <TabsTrigger value="rejected">Rechazadas</TabsTrigger>
          </TabsList>

          <TabsContent value={activeTab} className="mt-6">
            {loading ? (
              <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
                {[1, 2, 3].map((i) => (
                  <div key={i} className="bg-white rounded-xl p-4 animate-pulse">
                    <div className="aspect-video bg-gray-200 rounded-lg mb-3" />
                    <div className="h-5 bg-gray-200 rounded w-3/4 mb-2" />
                    <div className="h-4 bg-gray-200 rounded w-1/2" />
                  </div>
                ))}
              </div>
            ) : businesses.length > 0 ? (
              <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
                {businesses.map((business) => {
                  const cat = categories.find(c => c.id === business.category);
                  return (
                    <div key={business.business_id} className="bg-white rounded-xl overflow-hidden shadow-sm border">
                      <div className="aspect-video bg-gray-100 relative">
                        {business.featured_image_url ? (
                          <img
                            src={getImageUrl(business.featured_image_url)}
                            alt={business.name}
                            className="w-full h-full object-cover"
                          />
                        ) : (
                          <div className="w-full h-full flex items-center justify-center">
                            <Building2 className="h-12 w-12 text-gray-300" />
                          </div>
                        )}
                        <Badge className={`absolute top-2 left-2 ${statusColors[business.status]}`}>
                          {statusLabels[business.status]}
                        </Badge>
                      </div>
                      <div className="p-4">
                        <h3 className="font-semibold text-brand-text">{business.name}</h3>
                        <p className="text-sm text-brand-textMuted mb-3">
                          {cat?.name || "Sin categoría"}
                          {business.municipio && ` • ${business.municipio}`}
                        </p>
                        
                        {/* Quick actions for pending */}
                        {business.status === "pending" && (
                          <div className="flex gap-2 mb-3">
                            <Button
                              size="sm"
                              className="flex-1 bg-green-500 hover:bg-green-600"
                              onClick={() => handleQuickStatus(business, "published")}
                            >
                              <Check className="h-4 w-4 mr-1" /> Aprobar
                            </Button>
                            <Button
                              size="sm"
                              variant="outline"
                              className="flex-1 border-red-300 text-red-500 hover:bg-red-50"
                              onClick={() => handleQuickStatus(business, "rejected")}
                            >
                              <XCircle className="h-4 w-4 mr-1" /> Rechazar
                            </Button>
                          </div>
                        )}
                        
                        {business.submitter_name && (
                          <p className="text-xs text-gray-400 mb-2">
                            Enviado por: {business.submitter_name}
                          </p>
                        )}
                        
                        <div className="flex gap-1">
                          <Button variant="ghost" size="icon" onClick={() => handleOpenDialog(business)}>
                            <Pencil className="h-4 w-4" />
                          </Button>
                          <Button
                            variant="ghost"
                            size="icon"
                            onClick={() => {
                              setSelectedBusiness(business);
                              setDeleteDialogOpen(true);
                            }}
                          >
                            <Trash2 className="h-4 w-4 text-red-500" />
                          </Button>
                        </div>
                      </div>
                    </div>
                  );
                })}
              </div>
            ) : (
              <div className="text-center py-12 bg-white rounded-xl">
                <Building2 className="h-12 w-12 text-gray-300 mx-auto mb-4" />
                <p className="text-gray-500">No hay empresas en esta categoría</p>
              </div>
            )}
          </TabsContent>
        </Tabs>
      </div>

      {/* Create/Edit Dialog */}
      <Dialog open={dialogOpen} onOpenChange={setDialogOpen}>
        <DialogContent className="max-w-2xl max-h-[90vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle>{selectedBusiness ? "Editar Empresa" : "Nueva Empresa"}</DialogTitle>
          </DialogHeader>
          <form onSubmit={handleSubmit} className="space-y-4">
            <div className="grid grid-cols-2 gap-4">
              <div className="col-span-2">
                <Label>Nombre *</Label>
                <Input
                  value={formData.name}
                  onChange={(e) => setFormData({ ...formData, name: e.target.value })}
                  placeholder="Nombre del negocio"
                />
              </div>
              
              <div className="col-span-2">
                <Label>Categoría *</Label>
                <Select value={formData.category} onValueChange={(v) => setFormData({ ...formData, category: v })}>
                  <SelectTrigger><SelectValue placeholder="Seleccionar" /></SelectTrigger>
                  <SelectContent>
                    {categories.map((c) => <SelectItem key={c.id} value={c.id}>{c.name}</SelectItem>)}
                  </SelectContent>
                </Select>
              </div>

              <div className="col-span-2">
                <Label>Descripción</Label>
                <Textarea
                  value={formData.description}
                  onChange={(e) => setFormData({ ...formData, description: e.target.value })}
                  rows={3}
                />
              </div>

              <div className="col-span-2">
                <Label>Servicios (separados por coma)</Label>
                <Input
                  value={formData.services}
                  onChange={(e) => setFormData({ ...formData, services: e.target.value })}
                />
              </div>

              <div>
                <Label>Teléfono</Label>
                <Input value={formData.phone} onChange={(e) => setFormData({ ...formData, phone: e.target.value })} />
              </div>
              <div>
                <Label>WhatsApp</Label>
                <Input value={formData.whatsapp} onChange={(e) => setFormData({ ...formData, whatsapp: e.target.value })} />
              </div>
              <div>
                <Label>Email</Label>
                <Input value={formData.email} onChange={(e) => setFormData({ ...formData, email: e.target.value })} />
              </div>
              <div>
                <Label>Sitio web</Label>
                <Input value={formData.website} onChange={(e) => setFormData({ ...formData, website: e.target.value })} />
              </div>
              <div className="col-span-2">
                <Label>Dirección</Label>
                <Input value={formData.address} onChange={(e) => setFormData({ ...formData, address: e.target.value })} />
              </div>
              <div>
                <Label>Municipio</Label>
                <Input value={formData.municipio} onChange={(e) => setFormData({ ...formData, municipio: e.target.value })} />
              </div>
              <div>
                <Label>Horario</Label>
                <Input value={formData.hours} onChange={(e) => setFormData({ ...formData, hours: e.target.value })} />
              </div>
              <div>
                <Label>Facebook</Label>
                <Input value={formData.facebook} onChange={(e) => setFormData({ ...formData, facebook: e.target.value })} />
              </div>
              <div>
                <Label>Instagram</Label>
                <Input value={formData.instagram} onChange={(e) => setFormData({ ...formData, instagram: e.target.value })} />
              </div>
            </div>

            {/* Location on Map */}
            <LocationInput
              lat={formData.lat}
              lng={formData.lng}
              onChange={({ lat, lng }) => setFormData({ ...formData, lat, lng })}
              label="Ubicación en Mapa"
            />

            {/* Featured Image */}
            <div>
              <Label>Imagen principal</Label>
              {formData.featured_image_url ? (
                <div className="relative mt-2 w-48">
                  <img src={getImageUrl(formData.featured_image_url)} alt="" className="w-full aspect-video object-cover rounded-lg" />
                  <Button type="button" variant="secondary" size="sm" className="absolute bottom-2 right-2" onClick={() => handleOpenMediaDialog("featured")}>
                    Cambiar
                  </Button>
                </div>
              ) : (
                <Button type="button" variant="outline" className="mt-2" onClick={() => handleOpenMediaDialog("featured")}>
                  Seleccionar imagen
                </Button>
              )}
            </div>

            {/* Gallery Images */}
            <div>
              <Label>Galería de fotos</Label>
              <div className="mt-2 space-y-3">
                {/* Gallery preview */}
                {formData.gallery_urls && formData.gallery_urls.length > 0 && (
                  <div className="grid grid-cols-4 gap-2">
                    {formData.gallery_urls.map((img, index) => (
                      <div key={img.id || index} className="relative aspect-square rounded-lg overflow-hidden group">
                        <img
                          src={getImageUrl(img.url || img)}
                          alt={`Galería ${index + 1}`}
                          className="w-full h-full object-cover"
                        />
                        <button
                          type="button"
                          onClick={() => handleRemoveGalleryImage(img.id)}
                          className="absolute top-1 right-1 bg-red-500 text-white rounded-full p-1 opacity-0 group-hover:opacity-100 transition-opacity"
                        >
                          <X className="h-3 w-3" />
                        </button>
                      </div>
                    ))}
                  </div>
                )}
                <Button
                  type="button"
                  variant="outline"
                  onClick={() => handleOpenMediaDialog("gallery")}
                  className="w-full"
                >
                  <Upload className="h-4 w-4 mr-2" />
                  {formData.gallery_urls?.length > 0 ? "Agregar más fotos" : "Agregar fotos a la galería"}
                </Button>
                {mediaSelectMode === "gallery" && mediaDialogOpen && (
                  <p className="text-xs text-gray-500">Selecciona múltiples imágenes para la galería. Cierra el diálogo cuando termines.</p>
                )}
              </div>
            </div>

            <div>
              <Label>Estado</Label>
              <Select value={formData.status} onValueChange={(v) => setFormData({ ...formData, status: v })}>
                <SelectTrigger><SelectValue /></SelectTrigger>
                <SelectContent>
                  <SelectItem value="pending">Pendiente</SelectItem>
                  <SelectItem value="published">Publicado</SelectItem>
                  <SelectItem value="rejected">Rechazado</SelectItem>
                </SelectContent>
              </Select>
            </div>

            <div className="flex justify-end gap-2 pt-4">
              <Button type="button" variant="outline" onClick={() => setDialogOpen(false)}>Cancelar</Button>
              <Button type="submit" disabled={saving}>{saving ? "Guardando..." : "Guardar"}</Button>
            </div>
          </form>
        </DialogContent>
      </Dialog>

      {/* Media Dialog */}
      <Dialog open={mediaDialogOpen} onOpenChange={setMediaDialogOpen}>
        <DialogContent className="max-w-4xl max-h-[80vh] overflow-y-auto">
          <DialogHeader><DialogTitle>Seleccionar imagen</DialogTitle></DialogHeader>
          <div className="border-2 border-dashed rounded-lg p-4 text-center mb-4">
            <input ref={fileInputRef} type="file" accept="image/*" onChange={handleFileUpload} className="hidden" id="biz-upload" />
            <label htmlFor="biz-upload" className="cursor-pointer">
              {uploading ? <Loader2 className="h-8 w-8 mx-auto animate-spin" /> : (
                <><Upload className="h-8 w-8 mx-auto text-gray-400 mb-2" /><span className="text-sm">Subir imagen</span></>
              )}
            </label>
          </div>
          <div className="grid grid-cols-5 gap-3">
            {mediaList.map((media) => (
              <button key={media.media_id} type="button" onClick={() => handleMediaSelect(media)} className="aspect-square rounded-lg overflow-hidden border-2 border-transparent hover:border-primary">
                <img src={getImageUrl(media.url)} alt="" className="w-full h-full object-cover" />
              </button>
            ))}
          </div>
        </DialogContent>
      </Dialog>

      {/* Delete Dialog */}
      <AlertDialog open={deleteDialogOpen} onOpenChange={setDeleteDialogOpen}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>¿Eliminar empresa?</AlertDialogTitle>
            <AlertDialogDescription>Esta acción no se puede deshacer.</AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>Cancelar</AlertDialogCancel>
            <AlertDialogAction onClick={handleDelete} className="bg-red-500 hover:bg-red-600">Eliminar</AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
        <AdminPagination
          page={page}
          total={total}
          limit={limit}
          onPageChange={setPage}
          onLimitChange={(l) => { setLimit(l); setPage(1); }}
          testId="directorio-pagination"
          label="negocios"
        />
    </AdminLayout>
  );
}
